import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, finalize, takeUntil } from 'rxjs';
import { ProspectFeature } from '../../../map/features/prospects/prospect-feature';
import { PlacePullService } from '../../../services/place-pull.service';
import { PullConfirmDialogComponent, PullDialogResult } from '../../pull-confirm-dialog/pull-confirm-dialog.component';

import { MapObjectColors } from '../../../map/core/map-object-colors';
import { AuthService } from '../../../services/auth.service';

interface CellSizeOption {
  label: string;
  zoom: number;
  approximateMeters: number;
}

/**
 * Prospect layer control: paint grid cells and pull places for them (OSM/Google).
 * The Prospect workflow owns these controls; LeadFeature
 * owns rendering for Lead data created by completed pulls.
 */
@Component({
  selector: 'app-prospect-control',
  templateUrl: './prospect-control.component.html',
  styleUrls: ['./prospect-control.component.css']
})
export class ProspectControlComponent implements OnInit, OnDestroy {
  constructor(
    private dialog: MatDialog,
    public prospectFeature: ProspectFeature,
    private pull: PlacePullService, public readonly auth: AuthService) { }

  get selection() { return this.prospectFeature.selection; }

  // Selectable cell size = an absolute tile zoom (smaller zoom = bigger cell).
  readonly cellSizeOptions: readonly CellSizeOption[] = [
    { label: 'Large', zoom: 15, approximateMeters: 1000 },
    { label: 'Medium', zoom: 16, approximateMeters: 500 },
    { label: 'Small', zoom: 17, approximateMeters: 250 },
  ];
  readonly gridColors = [
    { label: 'Blue', value: '#2c5fb3' },
    { label: 'Amber', value: MapObjectColors.copper },
    { label: 'Teal', value: MapObjectColors.seaPine },
    { label: 'Slate', value: MapObjectColors.deepSea },
  ];
  selectionCount = 0;

  @Output() pullStarted = new EventEmitter<string>();
  submitting = false;
  error = '';
  private readonly destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.selection.changed$.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.selectionCount = this.selection.count);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  // ---------------- Prospecting grid ----------------

  /** Secondary control: dim the basemap outside the selection (spotlight). */
  get dimSurroundings(): boolean {
    return this.prospectFeature.dimSurroundings;
  }

  onDimToggle(event: Event): void {
    this.prospectFeature.setDimSurroundings((event.target as HTMLInputElement).checked);
  }

  onCellSizeChange(zoom: number): void {
    // Cell size changes the brush; selection remains the default map interaction.
    this.prospectFeature.setCellZoom(Number(zoom));
  }

  // ---------------- Pull job ----------------

  openPullDialog(): void {
    if (this.selectionCount === 0 || this.submitting || !this.auth.hasPermission('Lead.Manage')) return;
    const cells = [...this.selection.cells];
    this.submitting = true;
    const ref = this.dialog.open(PullConfirmDialogComponent, {
      width: '24rem', data: { cellCount: cells.length },
    });
    ref.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe((result?: PullDialogResult) => {
      if (!result) { this.submitting = false; return; }
      this.error = '';
      this.pull.startPull({ source: result.source, cells, apiKeyId: result.apiKeyId })
        .pipe(takeUntil(this.destroyed$), finalize(() => this.submitting = false))
        .subscribe({
          next: ({ jobId }) => { this.pullStarted.emit(jobId); },
          error: () => this.error = 'Unable to start the pull. Please try again.',
        });
    });
  }
}
